import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Folder } from '../types';

interface FolderNavigationContextType {
  selectedFolder: Folder | null;
  folderHistory: Folder[];
  setSelectedFolder: (folder: Folder | null) => void;
  setFolderHistory: (history: Folder[]) => void;
  handleFolderClick: (folder: Folder) => void;
  handleBackToFolders: () => void;
  handleBreadcrumbClick: (index: number) => void;
  sidebarKey: number;
  setSidebarKey: (key: number) => void;
}

const FolderNavigationContext = createContext<FolderNavigationContextType | undefined>(undefined);

interface FolderNavigationProviderProps {
  children: ReactNode;
}

export const FolderNavigationProvider: React.FC<FolderNavigationProviderProps> = ({ children }) => {
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
  const [folderHistory, setFolderHistory] = useState<Folder[]>([]);
  const [sidebarKey, setSidebarKey] = useState(0);
  const navigate = useNavigate();

  const handleFolderClick = (folder: Folder) => {
    // Actualizar el historial de navegación
    setFolderHistory(prev => [...prev, folder]);
    setSelectedFolder(folder);
    // Navegar a la ruta de documentación
    navigate('/documentation');
  };

  const handleBackToFolders = () => {
    setSelectedFolder(null);
    setFolderHistory([]);
    // Navegar a la ruta de documentación
    navigate('/documentation');
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === 0) {
      // Volver al inicio
      setSelectedFolder(null);
      setFolderHistory([]);
      navigate('/documentation');
    } else {
      // Navegar a una carpeta específica del historial
      const targetFolder = folderHistory[index - 1];
      setSelectedFolder(targetFolder);
      setFolderHistory(prev => prev.slice(0, index));
      navigate('/documentation');
    }
  };

  const value: FolderNavigationContextType = {
    selectedFolder,
    folderHistory,
    setSelectedFolder,
    setFolderHistory,
    handleFolderClick,
    handleBackToFolders,
    handleBreadcrumbClick,
    sidebarKey,
    setSidebarKey,
  };

  return (
    <FolderNavigationContext.Provider value={value}>
      {children}
    </FolderNavigationContext.Provider>
  );
};

export const useFolderNavigation = (): FolderNavigationContextType => {
  const context = useContext(FolderNavigationContext);
  if (context === undefined) {
    throw new Error('useFolderNavigation must be used within a FolderNavigationProvider');
  }
  return context;
};